import type { ReactNode } from 'react';
import { Box, Container, Divider, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useGetAppInfo } from '@/api/generated/app-info/app-info';
import { ContentCard, LinkValue, PageHeading } from '@/components/common';
import { APP_NAME, APP_TAGLINE, LEGAL_LINKS, MANUFACTURER } from '@/config/appInfo';
import { formatDate } from '@/utils/format';

const pad = (value: number) => String(value).padStart(2, '0');

/** Oznaka builda iz vremena builda — npr. 2026.10.21-1430 (lokalno vrijeme). */
const buildLabel = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
};

const InfoField = ({
  label,
  value,
  onClick
}: {
  label: string;
  value: string;
  onClick?: () => void;
}) => (
  <Stack gap={4} style={{ minWidth: 0 }}>
    <Text
      fz={11}
      fw={600}
      lh="14px"
      tt="uppercase"
      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
    >
      {label}
    </Text>
    {value && onClick ? (
      <div>
        <LinkValue value={value} onClick={onClick} />
      </div>
    ) : (
      <Text
        fz={14}
        lh="20px"
        style={{ color: 'var(--color-text-primary)', wordBreak: 'break-word' }}
      >
        {value || '—'}
      </Text>
    )}
  </Stack>
);

const AboutCard = ({ children }: { children: ReactNode }) => (
  <ContentCard>
    <Stack gap={20} px={{ base: 20, sm: 28 }} py={{ base: 20, sm: 24 }}>
      {children}
    </Stack>
  </ContentCard>
);

const cardDivider = <Divider color="var(--color-border-soft)" />;

export const AboutAppPage = () => {
  const { t } = useTranslation();
  const { data: appInfo } = useGetAppInfo();

  return (
    <Container size="lg" px={0}>
      <Stack gap={40}>
        <PageHeading title={t('about.title')} subtitle={t('about.subtitle')} />

        <Stack gap={24} maw={760} w="100%">
          <AboutCard>
            <Group gap={16} wrap="nowrap">
              <Box
                w={56}
                h={56}
                style={{
                  flexShrink: 0,
                  borderRadius: 10,
                  backgroundColor: 'var(--color-accent-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Text fz={16} fw={700} style={{ color: 'var(--color-text-on-secondary)' }}>
                  CXM
                </Text>
              </Box>
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Text fz={16} fw={600} lh="22px" style={{ color: 'var(--color-text-primary)' }}>
                  {APP_NAME}
                </Text>
                <Text fz={13} lh="19px" style={{ color: 'var(--color-text-secondary)' }}>
                  {APP_TAGLINE}
                </Text>
              </Stack>
            </Group>
            {cardDivider}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24} verticalSpacing={16}>
              <InfoField label={t('about.version')} value={appInfo?.version ?? ''} />
              <InfoField label={t('about.releaseDate')} value={formatDate(__APP_BUILD_TIME__)} />
              <InfoField label={t('about.build')} value={buildLabel(__APP_BUILD_TIME__)} />
              <InfoField
                label={t('about.environment')}
                value={t(`about.env.${import.meta.env.VITE_APP_ENV ?? 'development'}`)}
              />
            </SimpleGrid>
          </AboutCard>

          <AboutCard>
            <Text fz={14} fw={700} lh="20px" style={{ color: 'var(--color-text-primary)' }}>
              {t('about.manufacturer')}
            </Text>
            {cardDivider}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24} verticalSpacing={16}>
              <InfoField label={t('about.name')} value={MANUFACTURER.name} />
              <InfoField label={t('about.taxId')} value={MANUFACTURER.taxId} />
              <InfoField label={t('about.address')} value={MANUFACTURER.address} />
              <InfoField
                label={t('about.website')}
                value={MANUFACTURER.website}
                onClick={() =>
                  window.open(`https://${MANUFACTURER.website}`, '_blank', 'noopener,noreferrer')
                }
              />
              <InfoField
                label={t('about.support')}
                value={MANUFACTURER.supportEmail}
                onClick={() => {
                  window.location.href = `mailto:${MANUFACTURER.supportEmail}`;
                }}
              />
            </SimpleGrid>
          </AboutCard>

          <Stack gap={8}>
            {LEGAL_LINKS.length > 0 && (
              <Group gap={20}>
                {LEGAL_LINKS.map((link) => (
                  <Text
                    key={link.labelKey}
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    fz={12}
                    fw={600}
                    td="underline"
                    style={{ color: 'var(--color-accent-primary-active)' }}
                  >
                    {t(link.labelKey)}
                  </Text>
                ))}
              </Group>
            )}
            <Text fz={12} lh="18px" style={{ color: 'var(--color-text-muted)' }}>
              {t('about.copyright', {
                year: new Date(__APP_BUILD_TIME__).getFullYear(),
                name: MANUFACTURER.name
              })}
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};
